import {
  useMemo,
  useState,
} from 'react'
import {
  Box,
  Center,
  Loader,
} from '@mantine/core'
import { DeviceModalMantine } from './DeviceInformationManager/DeviceModalMantine/DeviceModal'
import { DeviceInformationTable } from './DeviceInformationManager/DeviceInformationTable'
import {
  useDevicesLoading,
  useDevicesQuery,
  useDevicesRefresh,
  useVisibleDevices,
} from '@data/device/device'
import Device from '@data/device/model/device'
import { MonitorCog } from 'lucide-react'

const DeviceManagement = () => {
  const devices = useVisibleDevices()
  const { isLoading: loading } = useDevicesQuery()
  const loadingDevices = useDevicesLoading()
  const loadDevices = useDevicesRefresh()

  const [selectedDeviceId, setSelectedDeviceId] = useState<string>()

  // Derived, not stored. A refetch or a change of account scope replaces `devices`, and the
  // modal has to follow that list rather than hold a snapshot that can outlive it. Deriving
  // also means there is no separate "is the modal open" flag left to disagree with it.
  const selectedDevice = useMemo(
    () => devices.find((device) => device.deviceId === selectedDeviceId),
    [devices, selectedDeviceId],
  )

  function onRowClick(device: Device) {
    setSelectedDeviceId(device.deviceId)
  }

  function onHideDeviceModal() {
    setSelectedDeviceId(undefined)
  }

  return (
    <Box
      h='100%'
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
      }}
    >
      <h1>
        <MonitorCog
          size={34}
          style={{
            marginRight: '1rem',
            marginLeft: '1rem',
          }}
        />
        Device Management
      </h1>
      <Box
        className='container-fluid flex-container'
        style={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          minHeight: 0,
          minWidth: 0,
        }}
      >
        {/* `loading` is the first load only. `loadingDevices` also covers refetches, so
            gating this branch on it would unmount the table — and its sorting, expansion,
            and filter focus — every time the device list is refreshed. */}
        {loading ? (
          <Center mt='xl'>
            <Loader
              aria-label='Loading devices'
              role='status'
            />
          </Center>
        ) : (
          <Box
            style={{
              display: 'flex',
              flex: 1,
              minHeight: 0,
              minWidth: 0,
            }}
          >
            <DeviceInformationTable
              devices={devices}
              onDeviceClick={onRowClick}
              onRefresh={loadDevices}
              refreshing={loadingDevices}
            />
            {selectedDevice ? (
              <DeviceModalMantine
                opened
                onClose={onHideDeviceModal}
                device={selectedDevice}
              />
            ) : null}
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default DeviceManagement
